import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  MapPin, Navigation, Loader2, Save, Map, RefreshCw, CheckCircle2,
  Camera, Image as ImageIcon, X, Download, KeyRound, MessageCircle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import axios from "axios";
import MapPreviewModal from "./MapPreviewModal";
import { buildWhatsappUrl } from "@/lib/waUrl";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Build a Google Maps URL forced to Satellite layer.
 * data=!3m1!1e3 → mapType=satellite. z=20 high zoom.
 */
const buildSatelliteViewUrl = (lat, lng) =>
  `https://www.google.com/maps/place/${lat},${lng}/@${lat},${lng},20z/data=!3m1!1e3`;

/** Navigation URL — keeps Google Maps' Universal "directions" intent
 *  (mobile app will open in its last-used map type; user can switch to satellite). */
const buildNavigationUrl = (lat, lng) =>
  `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}&travelmode=driving&layer=satellite`;

/**
 * Compress image client-side before upload.
 * Target: max 800px width, JPEG ~0.75 quality (~150KB).
 */
const compressImage = (file, maxWidth = 800, quality = 0.75) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (ev) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        let { width, height } = img;
        if (width > maxWidth) {
          height = Math.round((height * maxWidth) / width);
          width = maxWidth;
        }
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL("image/jpeg", quality));
      };
      img.onerror = reject;
      img.src = ev.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

export default function LocationSaver({
  customerId,
  customerName = "",
  customerAddress = "",
  source = "PDAM",
  onWaNumberChange,
}) {
  const navigate = useNavigate();
  const userRole = (localStorage.getItem("user_role") || "").toLowerCase();
  const userToken = localStorage.getItem("user_token");
  const isAllowed = (userRole === "petugas" || userRole === "kasir") && !!userToken;
  const authToken = userToken;

  const [location, setLocation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [savingGps, setSavingGps] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [showUpdateCodeDialog, setShowUpdateCodeDialog] = useState(false);
  const [updateCode, setUpdateCode] = useState("");
  const [pendingPhotoSource, setPendingPhotoSource] = useState(null);
  const [showGpsCodeDialog, setShowGpsCodeDialog] = useState(false);
  const [gpsUpdateCode, setGpsUpdateCode] = useState("");

  // WhatsApp state
  const [waInput, setWaInput] = useState("");
  const [savingWa, setSavingWa] = useState(false);
  const [showWaCodeDialog, setShowWaCodeDialog] = useState(false);
  const [waUpdateCode, setWaUpdateCode] = useState("");
  const [editingWa, setEditingWa] = useState(false);

  // Map preview state
  const [mapPreview, setMapPreview] = useState({
    open: false,
    lat: null,
    lng: null,
    accuracy: null,
    code: "",
    readonly: false,
  });
  const [nearbyLocations, setNearbyLocations] = useState([]);
  const [acquiringGps, setAcquiringGps] = useState(false);

  // Detail modal for clicking a red marker popup
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailLoc, setDetailLoc] = useState(null);
  const [detailFullPhoto, setDetailFullPhoto] = useState(false);

  const openDetailFor = useCallback(async (n) => {
    if (!n?.customer_id) return;
    setDetailLoc({ ...n, photo: null, _loading: true });
    setDetailLoading(true);
    try {
      const res = await axios.get(
        `${API}/customer-locations/${encodeURIComponent(n.customer_id)}`,
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      if (res.data?.found) {
        setDetailLoc({ ...res.data.location, _loading: false });
      } else {
        setDetailLoc({ ...n, _loading: false });
      }
    } catch (e) {
      setDetailLoc({ ...n, _loading: false });
    } finally {
      setDetailLoading(false);
    }
  }, [authToken]);

  const galleryInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  const fetchLocation = useCallback(async () => {
    if (!customerId || !isAllowed) return;
    try {
      setLoading(true);
      const res = await axios.get(`${API}/peta-global/customer/${encodeURIComponent(customerId)}`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      const doc = res.data || {};
      // Map peta-global master fields → location state shape used by this component
      setLocation({
        customer_id: doc.customer_id,
        latitude: doc.latitude ?? null,
        longitude: doc.longitude ?? null,
        accuracy: doc.accuracy ?? null,
        updated_at: doc.updated_at || null,
        photo: doc.photo || null,
        photo_updated_at: doc.photo_updated_at || null,
        whatsapp_number: doc.nomor_telp || "",
        whatsapp_updated_at: doc.nomor_telp_updated_at || doc.updated_at || null,
        customer_name: doc.nama || customerName || "",
        customer_address: doc.alamat || customerAddress || "",
      });
      if (onWaNumberChange) onWaNumberChange(doc.nomor_telp || "");
    } catch (e) {
      // 404 means customer is not yet tagged in master — treat as empty
      setLocation(null);
    } finally {
      setLoading(false);
    }
  }, [customerId, isAllowed, authToken, customerName, customerAddress, onWaNumberChange]);

  useEffect(() => {
    fetchLocation();
  }, [fetchLocation]);

  // Whenever the map modal opens, fetch ALL previously tagged customers
  // so the petugas can see every already-marked house on the map (not just nearby).
  useEffect(() => {
    if (!mapPreview.open || mapPreview.lat == null || mapPreview.lng == null) {
      setNearbyLocations([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/customer-locations/nearby`, {
          params: {
            lat: mapPreview.lat,
            lng: mapPreview.lng,
            exclude: customerId || "",
            all: true,
            limit: 2000,
          },
          headers: { Authorization: `Bearer ${authToken}` },
        });
        if (!cancelled) setNearbyLocations(res.data?.locations || []);
      } catch (e) {
        if (!cancelled) setNearbyLocations([]);
      }
    })();
    return () => { cancelled = true; };
  }, [mapPreview.open, mapPreview.lat, mapPreview.lng, customerId, authToken]);

  // Step 1: Acquire current GPS, then open map preview (with optional admin code)
  const acquireGpsThenPreview = async (codeOverride = "") => {
    if (!navigator.geolocation) return toast.error("Browser tidak mendukung GPS.");
    setAcquiringGps(true);
    const tid = toast.loading("Mengambil lokasi GPS...");
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        toast.dismiss(tid);
        setAcquiringGps(false);
        setMapPreview({
          open: true,
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
          code: codeOverride || "",
          readonly: false,
        });
      },
      (err) => {
        setAcquiringGps(false);
        const map = {
          1: "Izin lokasi ditolak. Aktifkan izin lokasi di browser.",
          2: "Lokasi tidak tersedia. Pastikan GPS aktif.",
          3: "Pengambilan lokasi timeout. Coba lagi.",
        };
        toast.error(map[err.code] || "Gagal mengambil lokasi GPS.", { id: tid });
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  };

  // Step 2: User confirms position from map preview → save to peta-global master
  const handleConfirmMapSave = async ({ latitude, longitude, accuracy }) => {
    if (!customerId) return toast.error("ID Pelanggan tidak tersedia.");
    setSavingGps(true);
    const tid = toast.loading("Menyimpan lokasi ke Peta Global...");
    try {
      // Include nama/alamat only when master record likely doesn't have them yet
      // (first-time tagging from PDAM Tebo). Backend's locked-field logic will
      // skip these fields when old values already exist.
      const payload = {
        customer_id: customerId,
        latitude,
        longitude,
        update_code: mapPreview.code || "",
      };
      if (!location || !location.customer_name) {
        if (customerName) payload.nama = customerName;
      }
      if (!location || !location.customer_address) {
        if (customerAddress) payload.alamat = customerAddress;
      }
      const res = await axios.put(
        `${API}/peta-global/customer/${encodeURIComponent(customerId)}`,
        payload,
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      const saved = res.data?.customer || {};
      setLocation((prev) => ({
        ...(prev || {}),
        latitude: saved.latitude ?? latitude,
        longitude: saved.longitude ?? longitude,
        accuracy,
        updated_at: saved.updated_at,
        customer_name: saved.nama || prev?.customer_name || "",
        customer_address: saved.alamat || prev?.customer_address || "",
      }));
      toast.success(res.data?.message || "Lokasi berhasil disimpan!", { id: tid });
      setMapPreview({ open: false, lat: null, lng: null, accuracy: null, code: "", readonly: false });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal menyimpan lokasi.", { id: tid });
    } finally {
      setSavingGps(false);
    }
  };

  const triggerLockGPS = () => {
    const hasGps = !!(location && location.latitude != null);
    if (hasGps) {
      // Need code from admin first
      setGpsUpdateCode("");
      setShowGpsCodeDialog(true);
    } else {
      // First time - go directly to GPS + preview
      acquireGpsThenPreview("");
    }
  };

  const handleConfirmGpsCode = () => {
    if (!gpsUpdateCode.trim()) {
      toast.error("Masukkan kode dari admin.");
      return;
    }
    const code = gpsUpdateCode.trim().toUpperCase();
    setShowGpsCodeDialog(false);
    acquireGpsThenPreview(code);
  };

  const openMaps = () => {
    if (!location) return;
    // Buka Peta Global terfokus ke pelanggan ini (bukan modal pin-drop lama).
    navigate(`/peta-global?focus=${encodeURIComponent(customerId)}`);
  };

  const openNavigation = () => {
    if (!location) return;
    window.open(
      buildNavigationUrl(location.latitude, location.longitude),
      "_blank", "noopener,noreferrer"
    );
  };

  const triggerPhotoPicker = (mode) => {
    const hasPhoto = !!location?.photo;
    if (hasPhoto) {
      setPendingPhotoSource(mode);
      setUpdateCode("");
      setShowUpdateCodeDialog(true);
    } else {
      if (mode === "camera") cameraInputRef.current?.click();
      else galleryInputRef.current?.click();
    }
  };

  const handleFileSelected = async (e, codeOverride = null) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("File harus berupa gambar.");
      return;
    }
    setUploadingPhoto(true);
    const tid = toast.loading("Mengompres & mengunggah foto ke Peta Global...");
    try {
      const dataUrl = await compressImage(file, 800, 0.75);
      const payload = {
        photo: dataUrl,
        update_code: codeOverride || "",
      };
      const res = await axios.post(
        `${API}/peta-global/customer/${encodeURIComponent(customerId)}/photo`,
        payload,
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      setLocation((prev) => ({
        ...(prev || { customer_id: customerId }),
        photo: dataUrl,
        photo_updated_at: res.data.photo_updated_at,
      }));
      toast.success(res.data.message || "Foto berhasil disimpan.", { id: tid });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal mengunggah foto.", { id: tid });
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleConfirmUpdateCode = () => {
    if (!updateCode.trim()) {
      toast.error("Masukkan kode dari admin.");
      return;
    }
    const code = updateCode.trim().toUpperCase();
    setShowUpdateCodeDialog(false);
    const input = pendingPhotoSource === "camera" ? cameraInputRef.current : galleryInputRef.current;
    if (input) {
      const onceHandler = (ev) => {
        input.removeEventListener("change", onceHandler);
        handleFileSelected(ev, code);
      };
      input.addEventListener("change", onceHandler);
      input.click();
    }
  };

  const downloadPhoto = () => {
    if (!location?.photo) return;
    const a = document.createElement("a");
    a.href = location.photo;
    a.download = `Rumah_${customerId || "pelanggan"}.jpg`;
    a.click();
  };

  // ====== WhatsApp (saved as nomor_telp in pdam_tebo_master) ======
  const saveWa = async (codeOverride = "") => {
    const trimmed = (waInput || "").trim();
    if (!trimmed) return toast.error("Masukkan No. WhatsApp.");
    setSavingWa(true);
    const tid = toast.loading("Menyimpan No. WhatsApp ke Peta Global...");
    try {
      const payload = {
        customer_id: customerId,
        nomor_telp: trimmed,
        update_code: codeOverride || "",
      };
      if (!location || !location.customer_name) {
        if (customerName) payload.nama = customerName;
      }
      if (!location || !location.customer_address) {
        if (customerAddress) payload.alamat = customerAddress;
      }
      const res = await axios.put(
        `${API}/peta-global/customer/${encodeURIComponent(customerId)}`,
        payload,
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      const saved = res.data?.customer || {};
      setLocation((prev) => ({
        ...(prev || { customer_id: customerId }),
        whatsapp_number: saved.nomor_telp || trimmed,
        whatsapp_updated_at: saved.updated_at || new Date().toISOString(),
      }));
      if (onWaNumberChange) onWaNumberChange(saved.nomor_telp || trimmed);
      toast.success(res.data?.message || "No. WA berhasil disimpan.", { id: tid });
      setWaInput("");
      setEditingWa(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal menyimpan No. WA.", { id: tid });
    } finally {
      setSavingWa(false);
    }
  };

  const triggerSaveWa = () => {
    const hasWa = !!location?.whatsapp_number;
    if (hasWa) {
      setWaUpdateCode("");
      setShowWaCodeDialog(true);
    } else {
      saveWa("");
    }
  };

  const handleConfirmWaCode = () => {
    if (!waUpdateCode.trim()) {
      toast.error("Masukkan kode dari admin.");
      return;
    }
    const code = waUpdateCode.trim().toUpperCase();
    setShowWaCodeDialog(false);
    saveWa(code);
  };

  const formatWaDisplay = (num) => {
    // 6281234567890 -> +62 812-3456-7890
    if (!num) return "";
    const s = String(num);
    if (s.length < 10) return s;
    return `+${s.slice(0, 2)} ${s.slice(2, 5)}-${s.slice(5, 9)}-${s.slice(9)}`;
  };

  const openWaChat = () => {
    if (!location?.whatsapp_number) return;
    window.open(buildWhatsappUrl(location.whatsapp_number), "_blank", "noopener,noreferrer");
  };

  if (!isAllowed) return null;

  const hasPhoto = !!location?.photo;

  return (
    <div
      className="mt-3 p-3 rounded-xl border border-cyan-200 bg-cyan-50/60"
      data-testid="location-saver"
    >
      {/* GPS section */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2 text-cyan-800">
          <MapPin className="w-4 h-4" />
          <span className="text-sm font-semibold">Lokasi Pelanggan (GPS)</span>
        </div>
        {location && location.latitude != null && (
          <span className="text-xs text-emerald-700 flex items-center gap-1">
            <CheckCircle2 className="w-3.5 h-3.5" />
            Tersimpan
          </span>
        )}
      </div>

      {loading ? (
        <div className="text-xs text-slate-500 flex items-center gap-2">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Memuat lokasi...
        </div>
      ) : location && location.latitude != null ? (
        <>
          <div className="text-xs text-slate-600 mb-2 break-all">
            <div><span className="font-medium">Koordinat:</span> {location.latitude?.toFixed(6)}, {location.longitude?.toFixed(6)}</div>
            {location.accuracy != null && (
              <div><span className="font-medium">Akurasi:</span> ±{Math.round(location.accuracy)} m</div>
            )}
            {location.updated_at && (
              <div><span className="font-medium">Diperbarui:</span> {new Date(location.updated_at).toLocaleString("id-ID")}</div>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" className="gap-1 border-cyan-300 text-cyan-700 hover:bg-cyan-100" onClick={openMaps} data-testid="view-location-btn">
              <Map className="w-3.5 h-3.5" /> Lihat (Satelit)
            </Button>
            <Button size="sm" variant="outline" className="gap-1 border-emerald-300 text-emerald-700 hover:bg-emerald-100" onClick={openNavigation} data-testid="navigate-location-btn">
              <Navigation className="w-3.5 h-3.5" /> Navigasi
            </Button>
            <Button size="sm" variant="outline" className="gap-1 border-amber-300 text-amber-700 hover:bg-amber-100"
              onClick={triggerLockGPS}
              disabled={savingGps || acquiringGps}
              data-testid="update-location-btn">
              {(savingGps || acquiringGps) ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
              Perbarui Lokasi
            </Button>
          </div>
          <p className="text-[11px] text-amber-700 italic flex items-center gap-1 mt-1">
            <KeyRound className="w-3 h-3" /> Update lokasi memerlukan kode dari admin.
          </p>
        </>
      ) : (
        <>
          <p className="text-xs text-slate-600 mb-2">Belum ada lokasi tersimpan. Klik tombol di bawah saat berada di rumah pelanggan — peta satelit akan tampil agar bisa di-geser sesuai posisi.</p>
          <Button size="sm" className="gap-1 bg-cyan-600 hover:bg-cyan-700 text-white"
            onClick={triggerLockGPS}
            disabled={savingGps || acquiringGps}
            data-testid="lock-gps-btn">
            {(savingGps || acquiringGps) ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            Lock & Simpan GPS
          </Button>
        </>
      )}

      {/* PHOTO section */}
      <div className="mt-4 pt-3 border-t border-cyan-200">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2 text-cyan-800">
            <Camera className="w-4 h-4" />
            <span className="text-sm font-semibold">Foto Rumah Pelanggan</span>
          </div>
          {hasPhoto && (
            <span className="text-xs text-emerald-700 flex items-center gap-1">
              <CheckCircle2 className="w-3.5 h-3.5" /> Tersimpan
            </span>
          )}
        </div>

        {hasPhoto ? (
          <div className="space-y-2">
            <button
              type="button"
              onClick={() => setShowPhotoModal(true)}
              className="block w-full max-w-[180px] rounded-lg overflow-hidden border-2 border-cyan-200 hover:border-cyan-400 transition"
              data-testid="photo-thumbnail"
            >
              <img src={location.photo} alt="Foto rumah" className="w-full h-auto object-cover" />
            </button>
            {location.photo_updated_at && (
              <p className="text-[11px] text-slate-500">
                Diperbarui: {new Date(location.photo_updated_at).toLocaleString("id-ID")}
              </p>
            )}
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="outline" className="gap-1 border-amber-300 text-amber-700 hover:bg-amber-100"
                onClick={() => triggerPhotoPicker("camera")} disabled={uploadingPhoto}
                data-testid="update-photo-camera-btn">
                {uploadingPhoto ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Camera className="w-3.5 h-3.5" />}
                Update Foto (Kamera)
              </Button>
              <Button size="sm" variant="outline" className="gap-1 border-amber-300 text-amber-700 hover:bg-amber-100"
                onClick={() => triggerPhotoPicker("gallery")} disabled={uploadingPhoto}
                data-testid="update-photo-gallery-btn">
                <ImageIcon className="w-3.5 h-3.5" /> Update dari Galeri
              </Button>
            </div>
            <p className="text-[11px] text-amber-700 italic flex items-center gap-1">
              <KeyRound className="w-3 h-3" /> Update foto memerlukan kode dari admin.
            </p>
          </div>
        ) : (
          <>
            <p className="text-xs text-slate-600 mb-2">Belum ada foto. Upload foto pertama tanpa kode admin.</p>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" className="gap-1 bg-cyan-600 hover:bg-cyan-700 text-white"
                onClick={() => triggerPhotoPicker("camera")} disabled={uploadingPhoto}
                data-testid="upload-photo-camera-btn">
                {uploadingPhoto ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Camera className="w-3.5 h-3.5" />}
                Ambil Foto (Kamera)
              </Button>
              <Button size="sm" variant="outline" className="gap-1 border-cyan-300 text-cyan-700 hover:bg-cyan-100"
                onClick={() => triggerPhotoPicker("gallery")} disabled={uploadingPhoto}
                data-testid="upload-photo-gallery-btn">
                <ImageIcon className="w-3.5 h-3.5" /> Pilih dari Galeri
              </Button>
            </div>
          </>
        )}
      </div>

      {/* WHATSAPP section */}
      <div className="mt-4 pt-3 border-t border-cyan-200">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2 text-cyan-800">
            <MessageCircle className="w-4 h-4" />
            <span className="text-sm font-semibold">No. WhatsApp Pelanggan</span>
          </div>
          {location?.whatsapp_number && !editingWa && (
            <span className="text-xs text-emerald-700 flex items-center gap-1">
              <CheckCircle2 className="w-3.5 h-3.5" /> Tersimpan
            </span>
          )}
        </div>

        {location?.whatsapp_number && !editingWa ? (
          <div className="space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={openWaChat}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-full bg-[#25D366] hover:bg-[#1ebe59] text-white shadow-sm transition"
                data-testid="wa-logo-btn"
                title="Buka chat WhatsApp"
              >
                <MessageCircle className="w-4 h-4" />
                <span className="font-semibold text-sm">{formatWaDisplay(location.whatsapp_number)}</span>
              </button>
              <Button size="sm" variant="outline" className="gap-1 border-amber-300 text-amber-700 hover:bg-amber-100"
                onClick={() => { setEditingWa(true); setWaInput(""); }}
                data-testid="update-wa-btn">
                <RefreshCw className="w-3.5 h-3.5" /> Perbarui
              </Button>
            </div>
            {location.whatsapp_updated_at && (
              <p className="text-[11px] text-slate-500">
                Diperbarui: {new Date(location.whatsapp_updated_at).toLocaleString("id-ID")}
              </p>
            )}
            <p className="text-[11px] text-amber-700 italic flex items-center gap-1">
              <KeyRound className="w-3 h-3" /> Update No. WA memerlukan kode dari admin.
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {!location?.whatsapp_number && (
              <p className="text-xs text-slate-600">
                Belum ada No. WhatsApp. Simpan pertama kali tanpa kode admin. Setelah tersimpan akan otomatis dipakai di Share Tagihan.
              </p>
            )}
            <div className="flex gap-2">
              <Input
                type="tel"
                inputMode="numeric"
                value={waInput}
                onChange={(e) => setWaInput(e.target.value.replace(/[^0-9+\s-]/g, ""))}
                placeholder="Contoh: 081234567890"
                className="h-9 text-sm flex-1"
                data-testid="wa-input"
                disabled={savingWa}
              />
              <Button size="sm"
                className="gap-1 bg-[#25D366] hover:bg-[#1ebe59] text-white"
                onClick={triggerSaveWa}
                disabled={savingWa || !waInput.trim()}
                data-testid="save-wa-btn">
                {savingWa ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                {location?.whatsapp_number ? "Perbarui" : "Simpan"}
              </Button>
            </div>
            {editingWa && (
              <Button size="sm" variant="ghost" className="text-xs"
                onClick={() => { setEditingWa(false); setWaInput(""); }}>
                Batal
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Hidden file inputs */}
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(e) => handleFileSelected(e)}
        data-testid="camera-input"
      />
      <input
        ref={galleryInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => handleFileSelected(e)}
        data-testid="gallery-input"
      />

      {/* Map preview modal (satellite + draggable pin, or read-only viewer) */}
      <MapPreviewModal
        open={mapPreview.open}
        initialLat={mapPreview.lat}
        initialLng={mapPreview.lng}
        initialAccuracy={mapPreview.accuracy}
        onCancel={() => setMapPreview({ open: false, lat: null, lng: null, accuracy: null, code: "", readonly: false })}
        onConfirm={handleConfirmMapSave}
        title={
          mapPreview.readonly
            ? `Lokasi ${customerName || customerId}`
            : (location && location.latitude != null ? "Perbarui Lokasi (Mode Satelit)" : "Tentukan Lokasi (Mode Satelit)")
        }
        subtitle={mapPreview.readonly ? (customerAddress || "Tampilan satelit lokasi tersimpan") : null}
        confirmLabel="Simpan Lokasi"
        saving={savingGps}
        readonly={mapPreview.readonly}
        nearby={nearbyLocations}
        onDetailClick={openDetailFor}
      />

      {/* Photo full-screen modal */}
      {showPhotoModal && hasPhoto && (
        <div
          className="fixed inset-0 z-[100] bg-black/85 flex items-center justify-center p-4"
          onClick={() => setShowPhotoModal(false)}
          data-testid="photo-modal"
        >
          <div className="relative max-w-3xl w-full max-h-full" onClick={(e) => e.stopPropagation()}>
            <button
              className="absolute -top-2 -right-2 bg-white rounded-full p-1.5 shadow-lg z-10"
              onClick={() => setShowPhotoModal(false)}
              data-testid="close-photo-modal"
            >
              <X className="w-5 h-5 text-slate-700" />
            </button>
            <img src={location.photo} alt="Foto rumah pelanggan" className="w-full h-auto max-h-[80vh] object-contain rounded-lg shadow-2xl bg-white" />
            <div className="mt-3 flex items-center justify-between gap-2 text-white text-xs flex-wrap">
              <div>
                <div className="font-semibold text-sm">{customerName || customerId}</div>
                {customerAddress && <div className="opacity-80">{customerAddress}</div>}
                {location.photo_updated_at && (
                  <div className="opacity-70">Diperbarui: {new Date(location.photo_updated_at).toLocaleString("id-ID")}</div>
                )}
              </div>
              <Button size="sm" className="gap-1 bg-white text-slate-800 hover:bg-slate-100" onClick={downloadPhoto} data-testid="download-photo-btn">
                <Download className="w-3.5 h-3.5" /> Download
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Update code dialog (PHOTO) */}
      {showUpdateCodeDialog && (
        <div className="fixed inset-0 z-[100] bg-black/60 flex items-center justify-center p-4" data-testid="update-code-dialog">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm overflow-hidden">
            <div className="bg-amber-500 text-white p-4 text-center">
              <KeyRound className="w-8 h-8 mx-auto mb-1" />
              <h3 className="font-bold">Kode Update Foto</h3>
              <p className="text-xs opacity-90">Masukkan kode 6 karakter dari admin</p>
            </div>
            <div className="p-4 space-y-3">
              <Input
                value={updateCode}
                onChange={(e) => setUpdateCode(e.target.value.toUpperCase())}
                placeholder="Contoh: AB12CD"
                maxLength={6}
                className="text-center text-lg font-mono tracking-widest"
                autoFocus
                data-testid="update-code-input"
              />
              <div className="flex gap-2">
                <Button variant="outline" className="flex-1" onClick={() => setShowUpdateCodeDialog(false)} data-testid="cancel-update-code">
                  Batal
                </Button>
                <Button className="flex-1 bg-amber-500 hover:bg-amber-600 text-white" onClick={handleConfirmUpdateCode} data-testid="confirm-update-code">
                  Lanjut Pilih Foto
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Update code dialog (GPS) */}
      {showGpsCodeDialog && (
        <div className="fixed inset-0 z-[100] bg-black/60 flex items-center justify-center p-4" data-testid="gps-code-dialog">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm overflow-hidden">
            <div className="bg-purple-600 text-white p-4 text-center">
              <KeyRound className="w-8 h-8 mx-auto mb-1" />
              <h3 className="font-bold">Kode Update Lokasi GPS</h3>
              <p className="text-xs opacity-90">Masukkan kode 6 karakter dari admin</p>
            </div>
            <div className="p-4 space-y-3">
              <Input
                value={gpsUpdateCode}
                onChange={(e) => setGpsUpdateCode(e.target.value.toUpperCase())}
                placeholder="Contoh: AB12CD"
                maxLength={6}
                className="text-center text-lg font-mono tracking-widest"
                autoFocus
                data-testid="gps-code-input"
              />
              <div className="flex gap-2">
                <Button variant="outline" className="flex-1" onClick={() => setShowGpsCodeDialog(false)} data-testid="cancel-gps-code">
                  Batal
                </Button>
                <Button className="flex-1 bg-purple-600 hover:bg-purple-700 text-white" onClick={handleConfirmGpsCode} data-testid="confirm-gps-code">
                  Lanjut Ambil GPS
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Update code dialog (WHATSAPP) */}
      {showWaCodeDialog && (
        <div className="fixed inset-0 z-[100] bg-black/60 flex items-center justify-center p-4" data-testid="wa-code-dialog">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm overflow-hidden">
            <div className="bg-[#25D366] text-white p-4 text-center">
              <KeyRound className="w-8 h-8 mx-auto mb-1" />
              <h3 className="font-bold">Kode Update No. WhatsApp</h3>
              <p className="text-xs opacity-90">Masukkan kode 6 karakter dari admin</p>
            </div>
            <div className="p-4 space-y-3">
              <Input
                value={waUpdateCode}
                onChange={(e) => setWaUpdateCode(e.target.value.toUpperCase())}
                placeholder="Contoh: AB12CD"
                maxLength={6}
                className="text-center text-lg font-mono tracking-widest"
                autoFocus
                data-testid="wa-code-input"
              />
              <div className="flex gap-2">
                <Button variant="outline" className="flex-1" onClick={() => setShowWaCodeDialog(false)} data-testid="cancel-wa-code">
                  Batal
                </Button>
                <Button className="flex-1 bg-[#25D366] hover:bg-[#1ebe59] text-white" onClick={handleConfirmWaCode} data-testid="confirm-wa-code">
                  Simpan No. WA
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Detail modal for clicked red marker (Sudah Tertandai) */}
      {detailLoc && (
        <div
          className="fixed inset-0 z-[140] bg-black/70 flex items-center justify-center p-4"
          onClick={() => { setDetailLoc(null); setDetailFullPhoto(false); }}
          data-testid="customer-detail-modal"
        >
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="bg-gradient-to-r from-cyan-600 to-emerald-600 text-white p-4 flex items-center justify-between">
              <div className="flex items-center gap-2 min-w-0">
                <MapPin className="w-5 h-5 flex-shrink-0" />
                <div className="min-w-0">
                  <div className="font-semibold text-sm truncate">{detailLoc.customer_name || detailLoc.customer_id}</div>
                  <div className="text-[11px] opacity-90 font-mono">ID: {detailLoc.customer_id}</div>
                </div>
              </div>
              <button
                className="p-1 rounded hover:bg-white/20"
                onClick={() => { setDetailLoc(null); setDetailFullPhoto(false); }}
                data-testid="close-customer-detail"
                aria-label="Tutup"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 space-y-3">
              {/* Status */}
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-500">Status:</span>
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-xs font-semibold">
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  Aktif (Sudah Tertandai)
                </span>
              </div>

              {detailLoc.customer_address && (
                <div className="text-xs text-slate-600">
                  <span className="text-slate-500">Alamat:</span> {detailLoc.customer_address}
                </div>
              )}

              {/* House photo */}
              <div>
                <div className="text-xs text-slate-500 mb-1.5 flex items-center gap-1">
                  <Camera className="w-3.5 h-3.5" /> Foto Rumah
                </div>
                {detailLoading ? (
                  <div className="h-40 rounded-lg border border-dashed border-slate-200 flex items-center justify-center text-slate-400 text-xs">
                    <Loader2 className="w-4 h-4 animate-spin mr-1" /> Memuat foto...
                  </div>
                ) : detailLoc.photo ? (
                  <button
                    type="button"
                    onClick={() => setDetailFullPhoto(true)}
                    className="block w-full rounded-lg overflow-hidden border-2 border-cyan-200 hover:border-cyan-400 transition"
                    data-testid="detail-photo-thumb"
                  >
                    <img src={detailLoc.photo} alt="Foto rumah" className="w-full h-auto object-cover max-h-64" />
                  </button>
                ) : (
                  <div className="h-32 rounded-lg border border-dashed border-slate-200 flex flex-col items-center justify-center text-slate-400 text-xs">
                    <Camera className="w-6 h-6 mb-1" />
                    Belum ada foto rumah
                  </div>
                )}
              </div>

              {detailLoc.updated_at && (
                <div className="text-[11px] text-slate-500">
                  Lokasi diperbarui: {new Date(detailLoc.updated_at).toLocaleString("id-ID")}
                </div>
              )}
              {detailLoc.photo_updated_at && (
                <div className="text-[11px] text-slate-500">
                  Foto diperbarui: {new Date(detailLoc.photo_updated_at).toLocaleString("id-ID")}
                </div>
              )}

              <Button
                variant="outline"
                className="w-full"
                onClick={() => { setDetailLoc(null); setDetailFullPhoto(false); }}
                data-testid="close-customer-detail-btn"
              >
                Tutup
              </Button>
            </div>
          </div>

          {/* Fullscreen photo (over detail modal) */}
          {detailFullPhoto && detailLoc.photo && (
            <div
              className="fixed inset-0 z-[150] bg-black/90 flex items-center justify-center p-4"
              onClick={() => setDetailFullPhoto(false)}
            >
              <img src={detailLoc.photo} alt="Foto rumah" className="max-w-full max-h-full object-contain rounded-lg shadow-2xl" />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
